
import { useRef, useState } from "react";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { Button } from "./button";
import { cn } from "@/lib/utils";

interface ImageUploadProps {
  value: File | null;
  onChange: (file: File | null) => void;
  existingUrl?: string;
  existingUrlCleared?: boolean;
  onClearExistingUrl?: () => void;
  error?: string;
  disabled?: boolean;
  isUploading?: boolean;
  aspectRatio?: string;
  height?: string;
}

const MAX_FILE_SIZE = 3 * 1024 * 1024;
// SVG is deliberately absent: POST /v1/images rejects it with a 400
// because an SVG can carry executable script. Keep this in step with the
// formats that endpoint accepts.
const ALLOWED_FORMATS = ["image/jpeg", "image/png", "image/gif", "image/webp"];
const ALLOWED_EXTENSIONS = ["JPEG", "PNG", "GIF", "WebP"];

export function ImageUpload({ value, onChange, existingUrl, existingUrlCleared, onClearExistingUrl, error, disabled, isUploading = false, aspectRatio, height }: ImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLocalError(null);

    if (file.size > MAX_FILE_SIZE) {
      setLocalError("File size must be less than 3MB");
      return;
    }

    if (!ALLOWED_FORMATS.includes(file.type)) {
      setLocalError(`File must be one of: ${ALLOWED_EXTENSIONS.join(", ")}`);
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    onChange(file);
  };

  const handleRemove = () => {
    setPreview(null);
    setLocalError(null);
    onChange(null);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    if (existingUrl && onClearExistingUrl) {
      onClearExistingUrl();
    }
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const displayError = error || localError;

  return (
    <div className="space-y-2">
      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_FORMATS.join(",")}
        onChange={handleFileChange}
        disabled={disabled}
        className="sr-only"
        aria-label="Upload image"
      />

      {!value && !preview && (!existingUrl || existingUrlCleared) ? (
        <button
          type="button"
          onClick={handleClick}
          disabled={disabled}
          className={cn(
            "relative flex w-full cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-black/15 bg-muted/40 px-4 transition-colors",
            !aspectRatio && !height && "h-40",
            "hover:border-black/25 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            disabled && "cursor-not-allowed opacity-50 hover:border-black/15 hover:bg-muted/40",
            displayError && "border-destructive"
          )}
          style={{ aspectRatio: aspectRatio || undefined, height: height || undefined }}
          aria-describedby={displayError ? "image-upload-error" : undefined}
        >
          <span className="mb-3 flex size-9 items-center justify-center rounded-lg bg-background text-muted-foreground shadow-control">
            <Upload className="size-4" aria-hidden="true" />
          </span>
          <p className="mb-1 text-[13px] font-medium text-foreground">
            Click to upload image
          </p>
          <p className="text-xs leading-relaxed text-muted-foreground">
            {ALLOWED_EXTENSIONS.join(", ")}<br/>
            (max 3MB)
          </p>
        </button>
      ) : (
        <div
          className={cn(
            "relative w-full overflow-hidden rounded-lg ring-1 ring-black/[0.06]",
            !aspectRatio && !height && "h-40"
          )}
          style={{ aspectRatio: aspectRatio || undefined, height: height || undefined }}
        >
          {preview ? (
            <img
              src={preview}
              alt="Preview"
              className="w-full h-full object-contain bg-muted"
            />
          ) : existingUrl && !existingUrlCleared ? (
            <img
              src={existingUrl}
              alt="Current image"
              className="w-full h-full object-contain bg-muted"
            />
          ) : (
            <div className="flex items-center justify-center w-full h-full bg-muted">
              <ImageIcon className="size-6 text-muted-foreground" aria-hidden="true" />
            </div>
          )}
          {isUploading && (
            <div className="absolute inset-0 bg-background/80 flex flex-col items-center justify-center gap-2">
              <Loader2 className="size-5 animate-spin text-muted-foreground" />
              <p className="text-[13px] font-medium text-foreground">Uploading...</p>
            </div>
          )}
          {!isUploading && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={handleRemove}
              disabled={disabled}
              className="absolute top-2 right-2 size-7"
              aria-label="Remove image"
            >
              <X className="size-3.5" />
            </Button>
          )}
        </div>
      )}

      {displayError && (
        <p id="image-upload-error" className="text-[13px] text-destructive" role="alert">
          {displayError}
        </p>
      )}
    </div>
  );
}
